
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  fetchEmployees, 
  fetchEmployeeById,
  createEmployee, 
  updateEmployee, 
  deleteEmployee 
} from "@/utils/supabase/employeeUtils";
import { Employee, EmployeeFilterOptions } from "@/types/employee";

// Define a query key factory to help with consistent cache management
const employeeKeys = {
  all: ['employees'] as const,
  lists: () => [...employeeKeys.all, 'list'] as const,
  list: (filters: EmployeeFilterOptions | undefined) => 
    [...employeeKeys.lists(), { filters }] as const,
  details: () => [...employeeKeys.all, 'detail'] as const,
  detail: (id: string) => [...employeeKeys.details(), id] as const,
};

export const useGetEmployees = (filters?: EmployeeFilterOptions) => {
  return useQuery({
    queryKey: employeeKeys.list(filters),
    queryFn: () => fetchEmployees(filters),
    staleTime: 7 * 24 * 60 * 60 * 1000, // 7 days (greatly extended from 24 hours)
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  });
};

export const useGetEmployeeById = (id: string) => {
  return useQuery({
    queryKey: employeeKeys.detail(id),
    queryFn: () => fetchEmployeeById(id),
    enabled: !!id,
    staleTime: 7 * 24 * 60 * 60 * 1000, // 7 days (greatly extended from 24 hours)
    gcTime: 30 * 24 * 60 * 60 * 1000, // 30 days
  });
};

export const useCreateEmployee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createEmployee,
    onSuccess: () => {
      // Invalidate all employee lists to ensure fresh data
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
};

export const useUpdateEmployee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: { id: string, employee: Partial<Employee> }) => 
      updateEmployee(data.id, data.employee),
    onSuccess: (_, variables) => {
      // Invalidate both the specific employee and all lists
      queryClient.invalidateQueries({ queryKey: employeeKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
};

export const useDeleteEmployee = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: deleteEmployee,
    onSuccess: () => {
      // Invalidate all employee lists when an employee is deleted
      queryClient.invalidateQueries({ queryKey: employeeKeys.lists() });
    },
  });
};
